import { useInfiniteQuery } from "@tanstack/react-query";
import {
  BriefcaseBusiness,
  Check,
  ChevronsUpDown,
  Files,
  FileText,
  Loader2,
} from "lucide-react";
import { FC, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetBillingUsage } from "~/api/billing/billing";
import { getListProjectsQueryKey, listProjects } from "~/api/folders/folders";
import { GetOrganization200OrganizationPlan } from "~/api/model";
import { useGetOrganization } from "~/api/organizations/organizations";
import { EmptySpace } from "~/components/EmptySpace";
import { Loader } from "~/components/Loader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Separator } from "~/components/ui/separator";
import { LIMITS } from "~/features/billing/constants";
import {
  getStoredLocale,
  processUsageData,
  useOrganizationParams,
} from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { cn } from "~/lib/utils";
import { MonthSelector } from "./usage/MonthSelector";
import { PlanUsageBar } from "./usage/PlanUsageBar";
import { SettingsChart } from "./usage/SettingsChart";

const PLANS_WITH_USAGE_LIMIT: ReadonlySet<GetOrganization200OrganizationPlan> =
  new Set([
    GetOrganization200OrganizationPlan.PRO,
    GetOrganization200OrganizationPlan.PREMIUM,
  ]);
type PlanWithUsageLimit = keyof typeof LIMITS;

const hasUsageLimit = (
  plan: GetOrganization200OrganizationPlan | undefined
): plan is PlanWithUsageLimit => !!plan && PLANS_WITH_USAGE_LIMIT.has(plan);

export const SettingsUsage: FC = () => {
  useTrackPageVisit("app/settings_usage/visit");
  const { organizationId } = useOrganizationParams();
  const { data: organizationInfo } = useGetOrganization(organizationId);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedCase, setSelectedCase] = useState<string>("all");
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const { from, to } = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(Date.UTC(year, month, 1, 0, 0, 0));
    const lastDay = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));

    return {
      from: firstDay.toISOString(),
      to: lastDay.toISOString(),
    };
  }, [selectedDate]);

  const intl = useIntl();
  const storedLocale = getStoredLocale() || "en";

  const { data, isLoading } = useGetBillingUsage(organizationId, { from, to });

  const PROJECTS_PER_PAGE = 100;

  const {
    data: casesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingCases,
  } = useInfiniteQuery({
    queryKey: getListProjectsQueryKey(organizationId, { view: "compact" }),
    queryFn: async ({ pageParam = 1 }) => {
      return listProjects(organizationId, {
        view: "compact",
        page: pageParam as number,
        limit: PROJECTS_PER_PAGE,
      });
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.pagination) return undefined;
      if (lastPage.pagination.currentPage < lastPage.pagination.totalPages) {
        return lastPage.pagination.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 50;
      if (nearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const projectOptions = useMemo(() => {
    const allOption = {
      value: "all",
      label: intl.formatMessage({
        id: "settings.usage.allCases",
        defaultMessage: "All Cases",
      }),
    };
    const options =
      casesData?.pages.flatMap((page) =>
        page.projects.map((project) => ({
          value: project.id,
          label: project.name,
        }))
      ) || [];
    return [allOption, ...options];
  }, [casesData, intl]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const filteredUsage =
      selectedCase === "all"
        ? data.usage
        : data.usage.filter((item) => item.folderId === selectedCase);
    return processUsageData({ ...data, usage: filteredUsage }, from);
  }, [data, selectedCase, from]);

  const pagesUsed = useMemo(() => {
    return data
      ? data.usage.reduce(
          (prevVal, currentVal) => prevVal + (currentVal.quantity ?? 0),
          0
        )
      : 0;
  }, [data]);

  const handleGetMonth = (from: string) => {
    setSelectedDate(new Date(from));
  };

  const organizationPlan = organizationInfo?.organizationPlan;
  const shouldShowPlanUsageBar = hasUsageLimit(organizationPlan);

  if (isLoadingCases)
    return (
      <div className="flex justify-center items-center w-full h-[50vh]">
        <Loader />
      </div>
    );

  const hasNoActivity =
    !isLoading &&
    data &&
    pagesUsed === 0 &&
    (data.counts?.cases ?? 0) === 0 &&
    (data.counts?.files ?? 0) === 0;

  return (
    <div className="flex flex-col gap-6 mt-2 min-w-0 overflow-hidden flex-1">
      <div className="flex flex-col gap-2">
        <h3 className="text-2xl font-semibold">
          <FormattedMessage id="settings.usage" defaultMessage={"Usage"} />
        </h3>
        <span className="text-sm text-muted-foreground">
          <FormattedMessage
            id="settings.usage.hint"
            defaultMessage={
              "AI computing activity across your cases and documents"
            }
          />
        </span>
      </div>
      {shouldShowPlanUsageBar && organizationPlan && (
        <PlanUsageBar
          pagesUsed={organizationInfo?.pagesUsedInThisBillingPeriod ?? 0}
          planPageLimit={LIMITS[organizationPlan]}
        />
      )}
      <div className="flex flex-wrap gap-4 items-center">
        <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={comboboxOpen}
              className="w-full sm:w-[280px] !h-10 justify-between font-normal"
            >
              <span className="truncate">
                {projectOptions.find((o) => o.value === selectedCase)?.label ??
                  intl.formatMessage({
                    id: "settings.usage.allCases",
                    defaultMessage: "All Cases",
                  })}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0">
            <Command>
              <CommandList onScroll={handleScroll}>
                <CommandEmpty>
                  <FormattedMessage
                    id="settings.usage.noCases"
                    defaultMessage="No cases found"
                  />
                </CommandEmpty>
                <CommandGroup>
                  {projectOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => {
                        setSelectedCase(option.value);
                        setComboboxOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedCase === option.value
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                  {isFetchingNextPage && (
                    <div className="flex justify-center py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Separator orientation="vertical" className="h-6 hidden sm:block" />
        <MonthSelector onMonthChange={handleGetMonth} />
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center w-full h-[50vh]">
          <Loader />
        </div>
      ) : hasNoActivity ? (
        <EmptySpace
          title={intl.formatMessage({
            id: "settings.usage.empty",
            defaultMessage: "No activity yet",
          })}
        />
      ) : (
        <div className="flex flex-col gap-6 min-w-0 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <Card className="w-full">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground font-semibold">
                      <FormattedMessage
                        id="settings.usage.pagesUsed"
                        defaultMessage={"Pages Processed"}
                      />
                    </span>
                    <Files className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span className="text-card-foregroud text-2xl font-semibold tracking-tight">
                    {pagesUsed.toLocaleString(storedLocale)}
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground font-semibold">
                      <FormattedMessage
                        id="settings.usage.casesCreated"
                        defaultMessage={"Cases Created"}
                      />
                    </span>
                    <BriefcaseBusiness className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span className="text-card-foregroud text-2xl font-semibold tracking-tight">
                    {data?.counts.cases?.toLocaleString(storedLocale)}
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full sm:col-span-2 lg:col-span-1">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground font-semibold">
                      <FormattedMessage
                        id="settings.usage.filesUploaded"
                        defaultMessage={"Documents Uploaded"}
                      />
                    </span>
                    <FileText className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span className="text-card-foregroud text-2xl font-semibold tracking-tight">
                    {data?.counts.files?.toLocaleString(storedLocale)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="w-full min-w-0 overflow-x-auto">
            <SettingsChart data={chartData} />
          </div>
        </div>
      )}
      <div className="flex gap-4 justify-between"></div>
    </div>
  );
};
